import { useState, useEffect } from "react";
import {
  AlertTriangle,
  BadgeCheck,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Coins,
  ExternalLink,
  Gift,
  History,
  Map,
  MapPin,
  RefreshCw,
  ShieldCheck,
  Star,
  Timer,
  UserCheck,
  UserRound,
  X,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  bangkokWorkDate,
  thaiTimeShort,
  workDuration,
  TabType,
  SystemFeatureFlags,
} from "./types";
import { openAttendanceConfirmation } from "@/lib/attendance-confirmation";
import { useLanguage } from "@/lib/i18n";

interface AttendanceTabProps {
  currentDisplayName: string;
  now: Date;
  status: any;
  today: any;
  canCheckIn: boolean;
  canCheckout: boolean;
  checkIn: any;
  checkout: any;
  idToken: string | null;
  setConfirmAction: (action: any) => void;
  setActiveTab: (tab: TabType) => void;
  entryAction?: "checkin" | "checkout" | null;
  myPerformance: any;
  myMonthlySummary?: any;
  performanceComments: Record<number, string>;
  setPerformanceComments: React.Dispatch<
    React.SetStateAction<Record<number, string>>
  >;
  commentPerformance: any;
  featureFlags?: SystemFeatureFlags;
  candidateNames?: string[];
  handleSaveFirstTimeName?: (name: string) => void;
}

export function AttendanceTab({
  currentDisplayName,
  now,
  status,
  today,
  canCheckIn,
  canCheckout,
  checkIn,
  checkout,
  idToken,
  setConfirmAction,
  setActiveTab,
  myPerformance,
  myMonthlySummary,
  performanceComments,
  setPerformanceComments,
  commentPerformance,
  featureFlags,
  candidateNames = [],
  handleSaveFirstTimeName,
  entryAction,
}: AttendanceTabProps) {
  const { t, language } = useLanguage();
  const tx = (th: string, my: string) => (language === "my" ? my : th);

  // Separate display mode for Check-in vs Check-out
  const [activeActionTab, setActiveActionTab] = useState<"checkin" | "checkout">(() => {
    if (entryAction === "checkout") return "checkout";
    if (entryAction === "checkin") return "checkin";
    if (today && !today.checkedOutAt) return "checkout";
    return "checkin";
  });

  useEffect(() => {
    if (entryAction) {
      setActiveActionTab(entryAction);
    }
  }, [entryAction]);

  // Modal Dialog states for mobile popups
  const [rulesOpen, setRulesOpen] = useState(false);
  const [nameSelectOpen, setNameSelectOpen] = useState(false);

  const [selectedCandidate, setSelectedCandidate] = useState("");
  const [liveCoords, setLiveCoords] = useState<{
    lat: number | null;
    lng: number | null;
    accuracy: number | null;
    loading: boolean;
  }>({
    lat: null,
    lng: null,
    accuracy: null,
    loading: true,
  });

  const refreshLocation = () => {
    if (typeof window !== "undefined" && navigator.geolocation) {
      setLiveCoords((prev) => ({ ...prev, loading: true }));
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLiveCoords({
            lat: Number(pos.coords.latitude.toFixed(6)),
            lng: Number(pos.coords.longitude.toFixed(6)),
            accuracy: Math.round(pos.coords.accuracy),
            loading: false,
          });
        },
        () => {
          setLiveCoords((prev) => ({ ...prev, loading: false }));
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 15000 }
      );
    } else {
      setLiveCoords((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    refreshLocation();
  }, []);

  const historyItems = status.data?.history || [];
  const reviews = myPerformance.data?.reviews || [];

  return (
    <div className="employee-module attendance-module reference-attendance space-y-3 pb-4">
      {/* Primary Compact Attendance Hero Card */}
      <Card className="employee-attendance-card reference-attendance-shell glass-panel overflow-visible rounded-2xl shadow-sm border border-[#e5ede7] py-0 gap-0">
        <div className="h-1 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-300" />

        <CardContent className="reference-attendance-content p-3 sm:p-4 space-y-3">
          {/* User Profile Header (Compact) */}
          <div className="reference-profile-card flex items-center justify-between gap-3 pb-2 border-b border-[#e7ede8]">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="reference-profile-avatar flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-900 border border-amber-200 shadow-2xs">
                <img src="/demo-mascot-attendance.png" alt="" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="reference-active-badge text-[10px] font-black tracking-wide text-emerald-800 bg-emerald-50 px-1.5 py-0.5 rounded-full border border-emerald-200 inline-flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    {t.activeEmployeeTitle}
                  </span>
                </div>
                <h2 className="reference-profile-name truncate text-sm sm:text-base font-black text-slate-900 leading-tight">
                  {currentDisplayName || (
                    <button
                      type="button"
                      onClick={() => setNameSelectOpen(true)}
                      className="text-amber-800 underline font-bold"
                    >
                      {t.selectNamePlaceholder}
                    </button>
                  )}
                </h2>
                <p className="reference-profile-department truncate text-xs font-semibold text-slate-500">
                  {status.data?.employee?.department || status.data?.employee?.employeeCode || tx("พนักงาน", "ဝန်ထမ်း")}
                </p>
              </div>
            </div>

            <div className="flex shrink-0 flex-col items-end gap-0.5">
              {today ? (
                today.checkedOutAt ? (
                  <Badge className="bg-amber-50 text-amber-950 hover:bg-amber-50 px-2 py-0.5 text-[10px] sm:text-xs font-black rounded-full border border-amber-200">
                    <CheckCircle2 size={12} className="mr-1 text-amber-600" />
                    {t.statusDone}
                  </Badge>
                ) : (
                  <Badge className="bg-amber-50 text-amber-950 hover:bg-amber-50 px-2 py-0.5 text-[10px] sm:text-xs font-black rounded-full border border-amber-200">
                    <Timer size={12} className="mr-1 text-amber-600" />
                    {t.statusWorking}
                  </Badge>
                )
              ) : (
                <Badge className="bg-amber-50 text-amber-800 hover:bg-amber-50 px-2 py-0.5 text-[10px] sm:text-xs font-black rounded-full border border-amber-200">
                  <Clock3 size={12} className="mr-1 text-amber-600" />
                  {t.statusNotStarted}
                </Badge>
              )}
              <span className="reference-profile-date inline-flex items-center gap-1 text-[9px] font-semibold text-slate-500">
                <CalendarDays size={13} />
                {status.data?.workDate || bangkokWorkDate(now)}
              </span>
            </div>
          </div>

          {/* Quick Notice if employee name is not set */}
          {!currentDisplayName && (
            <div
              onClick={() => setNameSelectOpen(true)}
              className="cursor-pointer rounded-xl bg-amber-50/90 border border-amber-300 p-2 text-xs flex items-center justify-between text-amber-900 transition hover:bg-amber-100"
            >
              <div className="flex items-center gap-1.5">
                <UserCheck size={14} className="text-amber-700" />
                <span className="font-bold text-[11px] sm:text-xs">
                  {tx("แตะที่นี่เพื่อลงทะเบียนชื่อพนักงานของคุณ", "ဝန်ထမ်းအမည် ရွေးချယ်ရန် ဤနေရာကို နှိပ်ပါ")}
                </span>
              </div>
              <ChevronRight size={14} className="text-amber-700" />
            </div>
          )}

          {/* Antixor Clean White Clock Bar */}
          <div className="employee-clock-card reference-clock-card rounded-xl bg-white py-2 px-3 text-slate-900 text-center shadow-xs relative overflow-hidden border border-amber-300/80 flex items-center justify-between">
            <div className="text-left">
              <span className="text-[9px] uppercase font-bold tracking-wider text-amber-800 inline-flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-ping" />
                {t.thailandStandardTime}
              </span>
              <p className="text-[11px] font-semibold text-slate-500 leading-tight">
                {now.toLocaleDateString(language === "my" ? "my-MM" : "th-TH", {
                  timeZone: "Asia/Bangkok",
                  weekday: "short",
                  day: "numeric",
                  month: "short",
                })}
              </p>
            </div>

            <div className="reference-clock-time text-2xl sm:text-3xl font-black font-numeric tabular-nums tracking-tight text-slate-900 leading-none">
              {now.toLocaleTimeString("th-TH", {
                timeZone: "Asia/Bangkok",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
              })}
            </div>
          </div>

          {/* Automatic Location Tracker Banner (Compact Pill) */}
          <div className="reference-location-card flex items-center justify-between rounded-lg bg-slate-100 border border-slate-200 px-2.5 py-1 text-[11px] text-slate-800">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="reference-location-icon inline-flex items-center justify-center shrink-0"><MapPin size={16} /></span>
              <span className="font-extrabold shrink-0 text-slate-800 text-[10px] sm:text-[11px]">
                {t.autoLocationLabel}
              </span>
              {liveCoords.loading ? (
                <span className="text-slate-500 flex items-center gap-1 truncate text-[10px]">
                  <RefreshCw size={11} className="animate-spin" /> {t.locationDetecting}
                </span>
              ) : liveCoords.lat != null ? (
                <span className="font-numeric font-bold text-emerald-900 truncate text-[10px] sm:text-[11px]">
                  {liveCoords.lat.toFixed(4)}, {liveCoords.lng?.toFixed(4)} (~{Math.round(liveCoords.accuracy ?? 0)}ม.)
                </span>
              ) : (
                <span className="text-emerald-800 font-bold truncate text-[10px]">
                  {t.locationReady}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={refreshLocation}
              title={t.locationRefreshTooltip}
              className="p-0.5 rounded hover:bg-emerald-100 text-emerald-800 transition shrink-0 ml-1"
            >
              <RefreshCw size={12} className={liveCoords.loading ? "animate-spin" : ""} />
            </button>
          </div>

          {/* Segmented Mode Switcher: แยกการแสดงผลและขนาด เช็คอิน vs เช็คเอาท์ */}
          <div className="grid grid-cols-2 p-1 bg-slate-100 rounded-2xl border border-slate-200/80 mb-2">
            <button
              type="button"
              onClick={() => {
                setActiveActionTab("checkin");
                window.history.replaceState({}, "", "/?module=attendance&action=checkin");
              }}
              className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-xs sm:text-sm font-black transition-all ${
                activeActionTab === "checkin"
                  ? "bg-white text-slate-900 shadow-xs border border-amber-200"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              <CheckCircle2 size={16} className={activeActionTab === "checkin" ? "text-amber-600" : "text-slate-400"} />
              <span>{tx("เช็คอินเข้างาน", "အလုပ်ဝင်")}</span>
              {today && <span className="h-2 w-2 rounded-full bg-amber-500" />}
            </button>

            <button
              type="button"
              onClick={() => {
                setActiveActionTab("checkout");
                window.history.replaceState({}, "", "/?module=attendance&action=checkout");
              }}
              className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-xs sm:text-sm font-black transition-all ${
                activeActionTab === "checkout"
                  ? "bg-white text-amber-800 shadow-xs border border-amber-200"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              <Clock3 size={16} className={activeActionTab === "checkout" ? "text-amber-700" : "text-slate-400"} />
              <span>{tx("เช็คเอาท์ออกงาน", "အလုပ်ဆင်း")}</span>
              {today?.checkedOutAt && <span className="h-2 w-2 rounded-full bg-amber-500" />}
            </button>
          </div>

          {/* DEDICATED VIEW 1: CHECK-IN MODE */}
          {activeActionTab === "checkin" && (
            <div className="space-y-3">
              {!today ? (
                <>
                  <div className="p-3.5 rounded-2xl bg-amber-50/70 border border-amber-200 flex items-center justify-between">
                    <div>
                      <span className="text-[10px] font-extrabold uppercase tracking-wider text-amber-800">
                        {tx("พร้อมลงเวลาเข้างาน", "အလုပ်ဝင်ချိန် မှတ်တမ်းတင်ရန် အသင့်ဖြစ်ပါပြီ")}
                      </span>
                      <p className="text-sm font-black text-slate-900 mt-0.5">
                        {tx("กดปุ่มด้านล่างเพื่อบันทึกเวลาเข้างาน", "အလုပ်ဝင်ရန် အောက်ပါခလုတ်ကို နှိပ်ပါ")}
                      </p>
                    </div>
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500 text-slate-950 font-black shadow-xs">
                      <Check size={20} strokeWidth={3} />
                    </div>
                  </div>

                  <Button
                    className={`w-full h-14 rounded-2xl text-base sm:text-lg font-black transition-all flex items-center justify-center gap-2 shadow-md ${
                      canCheckIn
                        ? "btn-primary-teal active:scale-[0.98] cursor-pointer shadow-amber-500/30 ring-2 ring-amber-500/20"
                        : "bg-slate-100 text-slate-400 hover:bg-slate-100 cursor-not-allowed border border-slate-200"
                    }`}
                    disabled={!canCheckIn}
                    onClick={() => {
                      if (!idToken && window.liff && !window.liff.isLoggedIn()) {
                        window.liff.login();
                        return;
                      }
                      setConfirmAction(openAttendanceConfirmation("checkin", canCheckIn));
                    }}
                  >
                    {checkIn.isPending ? (
                      <RefreshCw className="animate-spin mr-1" size={20} />
                    ) : (
                      <Check size={22} strokeWidth={3} className="mr-1" />
                    )}
                    <span>{t.btnCheckIn}</span>
                  </Button>
                </>
              ) : (
                <div className="p-4 rounded-2xl bg-amber-50/80 border border-amber-200 space-y-2 text-center">
                  <div className="flex items-center justify-center gap-2 text-amber-800 font-black text-base">
                    <CheckCircle2 size={22} />
                    <span>{t.btnCheckInDone}</span>
                  </div>
                  <p className="text-xs font-bold text-slate-600">
                    {tx("คุณได้ลงเวลาเข้างานเรียบร้อยแล้วเมื่อ", "အလုပ်ဝင်ချိန် မှတ်တမ်းတင်ပြီးပါပြီ")}{" "}
                    <span className="font-numeric font-black text-amber-800 text-sm">
                      {thaiTimeShort(today.checkedInAt)} น.
                    </span>
                  </p>
                  {today.checkInLat != null && today.checkInLng != null && (
                    <a
                      href={`https://www.google.com/maps?q=${today.checkInLat},${today.checkInLng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-800 underline hover:text-emerald-950 pt-0.5"
                    >
                      <MapPin size={12} /> {tx("ดูพิกัดสถานที่เข้างานบน Google Maps", "မြေပုံတွင် တည်နေရာကြည့်ရန်")}
                    </a>
                  )}
                  {!today.checkedOutAt && (
                    <div className="pt-2 border-t border-emerald-200/80">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setActiveActionTab("checkout");
                          window.history.replaceState({}, "", "/?module=attendance&action=checkout");
                        }}
                        className="w-full h-10 rounded-xl border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100 font-black text-xs"
                      >
                        <Clock3 size={15} className="mr-1.5 text-amber-700" />
                        {tx("สลับไปหน้าเช็คเอาท์ออกงาน ›", "အလုပ်ဆင်းချိန် မှတ်တမ်းသို့ သွားရန် ›")}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* DEDICATED VIEW 2: CHECK-OUT MODE */}
          {activeActionTab === "checkout" && (
            <div className="space-y-3">
              {today && !today.checkedOutAt ? (
                <>
                  <div className="p-3.5 rounded-2xl bg-amber-50/90 border border-amber-200 flex items-center justify-between">
                    <div>
                      <span className="text-[10px] font-extrabold uppercase tracking-wider text-amber-800">
                        {tx("กำลังปฏิบัติงาน", "အလုပ်လုပ်နေဆဲ")} · {tx("เริ่มงาน", "စတင်ချိန်")} {thaiTimeShort(today.checkedInAt)} น.
                      </span>
                      <p className="text-sm font-black text-slate-900 mt-0.5">
                        {tx("ทำงานมาแล้ว", "အလုပ်ချိန်")}: {workDuration(today.checkedInAt, now)}
                      </p>
                    </div>
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-600 text-white shadow-xs">
                      <Clock3 size={20} strokeWidth={2.5} />
                    </div>
                  </div>

                  <Button
                    className={`w-full h-14 rounded-2xl text-base sm:text-lg font-black transition-all flex items-center justify-center gap-2 shadow-md ${
                      canCheckout
                        ? "btn-primary-coral active:scale-[0.98] cursor-pointer shadow-amber-800/30 ring-2 ring-amber-600/20"
                        : "bg-slate-100 text-slate-400 hover:bg-slate-100 cursor-not-allowed border border-slate-200"
                    }`}
                    disabled={!canCheckout}
                    onClick={() => {
                      if (!idToken && window.liff && !window.liff.isLoggedIn()) {
                        window.liff.login();
                        return;
                      }
                      setConfirmAction(openAttendanceConfirmation("checkout", canCheckout));
                    }}
                  >
                    {checkout.isPending ? (
                      <RefreshCw className="animate-spin mr-1" size={20} />
                    ) : (
                      <Clock3 size={22} strokeWidth={2.5} className="mr-1" />
                    )}
                    <span>{t.btnCheckOut}</span>
                  </Button>
                </>
              ) : today?.checkedOutAt ? (
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-2 text-center">
                  <div className="flex items-center justify-center gap-2 text-emerald-800 font-black text-base">
                    <CheckCircle2 size={22} />
                    <span>{t.btnCheckOutDone}</span>
                  </div>
                  <p className="text-xs font-bold text-slate-600">
                    {tx("บันทึกเวลาออกงานเรียบร้อยแล้วเมื่อ", "အလုပ်ဆင်းချိန် မှတ်တမ်းတင်ပြီးပါပြီ")}{" "}
                    <span className="font-numeric font-black text-slate-900 text-sm">
                      {thaiTimeShort(today.checkedOutAt)} น.
                    </span>
                  </p>
                  <div className="p-2 rounded-xl bg-white border border-slate-200 text-xs font-semibold text-slate-600 inline-block">
                    {tx("รวมเวลาทำงานวันนี้", "ယနေ့ စုစုပေါင်း အလုပ်ချိန်")}:{" "}
                    <span className="font-black text-[#1b6b44]">
                      {workDuration(today.checkedInAt, today.checkedOutAt)}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-2xl bg-amber-50/80 border border-amber-200 space-y-2.5 text-center">
                  <div className="flex items-center justify-center gap-1.5 text-amber-900 font-black text-sm">
                    <AlertTriangle size={18} className="text-amber-600" />
                    <span>{tx("ยังไม่พบข้อมูลการเข้างานวันนี้", "ယနေ့ အလုပ်ဝင်ချိန် မရှိသေးပါ")}</span>
                  </div>
                  <p className="text-xs text-slate-600 font-medium">
                    {tx(
                      "คุณต้องลงเวลาเข้างานก่อน จึงจะสามารถบันทึกเวลาออกงานได้",
                      "အလုပ်ဆင်းချိန် မမှတ်တမ်းတင်မီ အလုပ်ဝင်ချိန်ကို အရင်မှတ်တမ်းတင်ပါ"
                    )}
                  </p>
                  <Button
                    type="button"
                    onClick={() => {
                      setActiveActionTab("checkin");
                      window.history.replaceState({}, "", "/?module=attendance&action=checkin");
                    }}
                    className="w-full h-11 rounded-xl btn-primary-teal font-black text-xs sm:text-sm"
                  >
                    <Check size={16} className="mr-1.5" strokeWidth={3} />
                    {tx("ไปที่หน้าเช็คอินเข้างาน", "အလုပ်ဝင်ချိန် မှတ်တမ်းသို့ သွားပါ")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* QUICK ACTION GRID (BUTTONS THAT OPEN POPUP MODALS) */}
      <div className="reference-quick-grid grid grid-cols-2 gap-3">
        {/* Button 1: Salary Advance */}
        {featureFlags?.enableSalaryAdvance !== false && (
          <button
            type="button"
            onClick={() => {
              window.history.pushState({}, "", "/?module=salary-advance");
              setActiveTab("salary-advance");
            }}
            className="reference-action-card reference-action-advance p-2.5 rounded-xl border border-amber-200/80 bg-gradient-to-br from-amber-50/60 to-white hover:border-amber-500/60 text-left transition shadow-2xs group flex items-center justify-between"
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-8 w-8 rounded-lg bg-amber-100 text-amber-900 flex items-center justify-center font-bold shrink-0">
                <Coins size={16} />
              </div>
              <div className="min-w-0">
                <h4 className="font-black text-xs text-slate-900 group-hover:text-amber-600 truncate">
                  {t.quickAdvanceTitle}
                </h4>
                <p className="text-[10px] text-slate-500 font-medium truncate">
                  {tx("เบิกตามวันทำงาน", "ကြိုတင်ငွေထုတ်")}
                </p>
              </div>
            </div>
            <ChevronRight size={14} className="text-slate-400 group-hover:text-amber-600 shrink-0" />
          </button>
        )}

        {/* Button 2: Welfare */}
        <button
          type="button"
          onClick={() => {
            window.history.pushState({}, "", "/?module=welfare");
            setActiveTab("welfare");
          }}
          className="reference-action-card reference-action-welfare p-2.5 rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50/70 to-white hover:border-amber-400 text-left transition shadow-2xs group flex items-center justify-between"
        >
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-8 w-8 rounded-lg bg-amber-100 text-amber-800 flex items-center justify-center font-bold shrink-0">
              <Gift size={16} />
            </div>
            <div className="min-w-0">
              <h4 className="font-black text-xs text-slate-900 group-hover:text-amber-800 truncate">
                {tx("สวัสดิการพนักงาน", "သက်သာချောင်ချိရေး")}
              </h4>
              <p className="text-[10px] text-slate-500 font-medium truncate">
                {tx("ขอเบิกสวัสดิการ", "ထောက်ပံ့ကြေး")}
              </p>
            </div>
          </div>
          <ChevronRight size={14} className="text-slate-400 group-hover:text-amber-700 shrink-0" />
        </button>

        {/* Button 3: Assigned Tasks & Site (Navigates to tasks) */}
        <button
          type="button"
          onClick={() => {
            window.history.pushState({}, "", "/?module=tasks");
            setActiveTab("tasks");
          }}
          className="reference-action-card reference-action-tasks p-2.5 rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50/70 to-white hover:border-blue-400 text-left transition shadow-2xs group flex items-center justify-between"
        >
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-8 w-8 rounded-lg bg-blue-100 text-blue-800 flex items-center justify-center font-bold shrink-0">
              <Zap size={16} />
            </div>
            <div className="min-w-0">
              <h4 className="font-black text-xs text-slate-900 group-hover:text-blue-800 truncate">
                {tx("งานที่ได้รับมอบหมาย", "တာဝန်နှင့် လုပ်ငန်းခွင်")}
              </h4>
              <p className="text-[10px] text-slate-500 font-medium truncate">
                {tx("ไซต์งานก่อสร้าง & OT", "ဆောက်လုပ်ရေးနှင့် OT")}
              </p>
            </div>
          </div>
          <ChevronRight size={14} className="text-slate-400 group-hover:text-blue-700 shrink-0" />
        </button>

        {/* Button 4: Work Safety Rules & OT (POPUP) */}
        <button
          type="button"
          onClick={() => setRulesOpen(true)}
          className="reference-action-card reference-action-rules p-2.5 rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white hover:border-slate-400 text-left transition shadow-2xs group flex items-center justify-between"
        >
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-8 w-8 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center font-bold shrink-0">
              <ShieldCheck size={16} />
            </div>
            <div className="min-w-0">
              <h4 className="font-black text-xs text-slate-900 group-hover:text-slate-800 truncate">
                {tx("ความปลอดภัย & OT", "လုံခြုံရေးနှင့် OT")}
              </h4>
              <p className="text-[10px] text-slate-500 font-medium truncate">
                {tx("กฎความปลอดภัยไซต์งาน", "ဆောက်လုပ်ရေး စည်းမျဉ်း")}
              </p>
            </div>
          </div>
          <ChevronRight size={14} className="text-slate-400 group-hover:text-slate-700 shrink-0" />
        </button>
      </div>

      <div className="reference-detail-stack space-y-3">
        <div className="reference-current-location">
          <div className="reference-detail-icon reference-detail-icon-blue">
            <MapPin size={24} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="reference-detail-label">{tx("ตำแหน่งปัจจุบัน", "လက်ရှိတည်နေရာ")}</p>
            <p className="reference-detail-value font-numeric">
              {liveCoords.lat != null
                ? `${liveCoords.lat.toFixed(4)}, ${liveCoords.lng?.toFixed(4)}`
                : tx("กำลังตรวจสอบตำแหน่ง", "တည်နေရာကို စစ်ဆေးနေသည်")}
            </p>
            {liveCoords.accuracy != null && (
              <p className="reference-detail-meta">{tx(`ความแม่นยำประมาณ ${Math.round(liveCoords.accuracy)} ม.`, `တိကျမှု ${Math.round(liveCoords.accuracy)} မီတာခန့်`)}</p>
            )}
          </div>
          {liveCoords.lat != null && liveCoords.lng != null ? (
            <a
              href={`https://www.google.com/maps?q=${liveCoords.lat},${liveCoords.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="reference-map-button"
            >
              <Map size={19} /> {tx("ดูแผนที่", "မြေပုံ")}
            </a>
          ) : (
            <button type="button" className="reference-map-button" onClick={refreshLocation}>
              <RefreshCw size={18} className={liveCoords.loading ? "animate-spin" : ""} />
              {tx("ตรวจใหม่", "ပြန်စစ်မည်")}
            </button>
          )}
        </div>

        <div className="reference-summary-card">
          <div className="reference-summary-item">
            <div className="reference-detail-icon reference-detail-icon-green">
              <CheckCircle2 size={22} />
            </div>
            <div>
              <p className="reference-detail-label">{tx("วันนี้", "ယနေ့")}</p>
              <p className="reference-detail-value">
                {today ? (today.checkedOutAt ? t.statusDone : t.statusWorking) : t.statusNotStarted}
              </p>
              <p className="reference-detail-meta">{today ? "1 " : "0 "}{tx("รายการ", "မှတ်တမ်း")}</p>
            </div>
          </div>
          <div className="reference-summary-divider" />
          <div className="reference-summary-item">
            <div className="reference-detail-icon reference-detail-icon-orange">
              <Timer size={22} />
            </div>
            <div>
              <p className="reference-detail-label">{tx("ชั่วโมงทำงานวันนี้", "ယနေ့ အလုပ်ချိန်")}</p>
              <p className="reference-detail-value">
                {today ? workDuration(today.checkedInAt, today.checkedOutAt) : "0 ชม."}
              </p>
              <p className="reference-detail-meta">
                {today?.checkedInAt
                  ? `${tx("เริ่มงาน", "စတင်ချိန်")} ${thaiTimeShort(today.checkedInAt)} น.`
                  : tx("ยังไม่เริ่มงาน", "မစတင်ရသေးပါ")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* POPUP MODAL: Work Safety Rules and Overtime */}
      <Dialog open={rulesOpen} onOpenChange={setRulesOpen}>
        <DialogContent className="max-w-md rounded-2xl p-4 sm:p-5">
          <DialogHeader className="pb-2 border-b border-slate-100 pr-8">
            <DialogTitle className="text-base font-black text-slate-900 flex items-center gap-2">
              <ShieldCheck size={18} className="text-emerald-700" />
              {t.workRulesTitle}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {tx("ข้อกำหนดความปลอดภัยและระเบียบไซต์งาน", "ဆောက်လုပ်ရေး စည်းမျဉ်းနှင့် လုံခြုံရေး")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 pt-2 text-xs text-slate-700 leading-relaxed">
            <div className="p-3 rounded-xl bg-amber-50 border border-amber-300 text-amber-950 space-y-1.5">
              <h5 className="font-black text-xs text-amber-900 flex items-center gap-1.5">
                <span>⛑️</span>
                <span>{tx("กฎความปลอดภัยไซต์งานก่อสร้าง (PPE)", "ဆောက်လုပ်ရေး လုပ်ငန်းခွင် လုံခြုံရေး (PPE)")}</span>
              </h5>
              <p className="text-[11px] font-medium leading-relaxed">
                {tx(
                  "พนักงานทุกคนต้องสวมหมวกนิรภัย รองเท้าเซฟตี้ เสื้อกั๊กสะท้อนแสง และใช้อุปกรณ์กันตกเมื่อทำงานบนที่สูงอย่างเคร่งครัด",
                  "ဝန်ထမ်းအားလုံး ဦးထုပ်၊ လုံခြုံရေးဖိနပ်၊ အလင်းပြန်အင်္ကျီနှင့် အမြင့်တွင် အလုပ်လုပ်ပါက ခါးပတ်ကြိုးကို မဖြစ်မနေ ဝတ်ဆင်ရမည်"
                )}
              </p>
            </div>

            <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-950 space-y-1">
              <h5 className="font-black text-xs text-emerald-900">
                {tx("🕒 เวลาเข้า-ออกงานมาตรฐาน & OT", "ပုံမှန် အလုပ်ချိန်နှင့် OT")}
              </h5>
              <p className="text-[11px] font-medium">
                {t.workRulesDesc}
              </p>
            </div>

            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 space-y-1">
              <h5 className="font-black text-xs text-slate-900">
                {tx("📍 การลงเวลาผ่านพิกัด GPS ประจำไซต์", "GPS တည်နေရာ")}
              </h5>
              <p className="text-[11px] font-medium text-slate-600">
                {tx(
                  "ระบบจะจับพิกัดจริงอัตโนมัติขณะกดเข้าและออกงาน เพื่อเป็นหลักฐานยืนยันว่าลงเวลาจากไซต์งานก่อสร้างจริง",
                  "စနစ်သည် လုပ်ငန်းခွင်သို့ အမှန်တကယ် ရောက်ရှိကြောင်း အတည်ပြုရန် တည်နေရာကို အလိုအလျောက် မှတ်သားပါသည်"
                )}
              </p>
            </div>

            <div className="p-3 rounded-xl bg-emerald-50/70 border border-emerald-200 text-emerald-950 space-y-1">
              <h5 className="font-black text-xs text-emerald-900">
                {tx("💰 การขอเบิกเงินล่วงหน้า (Salary Advance)", "ကြိုတင်ငွေထုတ်ခြင်း")}
              </h5>
              <p className="text-[11px] font-medium">
                {tx(
                  "พนักงานรายวันสามารถขอเบิกเงินล่วงหน้าได้ตามจำนวนวันทำงานจริงในรอบปัจจุบัน (ตัดรอบวันที่ 10 และ 25)",
                  "လက်ရှိ ကာလအတွင်း အမှန်တကယ် လုပ်ကိုင်ခဲ့သော ရက်များအတွက်သာ ကြိုတင်ငွေ ထုတ်ယူနိုင်ပါသည်"
                )}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* POPUP MODAL 4: Name Selection */}
      <Dialog open={nameSelectOpen} onOpenChange={setNameSelectOpen}>
        <DialogContent className="max-w-md rounded-2xl p-4 sm:p-5">
          <DialogHeader className="pb-1 pr-8">
            <DialogTitle className="text-base font-black text-emerald-950 flex items-center gap-2">
              <UserRound size={18} className="text-emerald-700" />
              {t.onboardingTitle}
            </DialogTitle>
            <DialogDescription className="text-xs text-emerald-800 font-semibold">
              {t.onboardingSub}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 pt-1">
            <div>
              <label className="block text-xs font-black text-slate-700 mb-1">
                {tx("เลือกชื่อของคุณจากรายชื่อในระบบ *", "အမည် ရွေးချယ်ပါ *")}
              </label>
              <select
                value={selectedCandidate}
                onChange={(e) => setSelectedCandidate(e.target.value)}
                className="w-full rounded-xl border-2 border-emerald-300 bg-white px-3 py-2 text-sm font-bold text-slate-900 shadow-2xs focus:border-emerald-600 focus:outline-none"
              >
                <option value="">{t.selectNamePlaceholder}</option>
                {candidateNames?.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            <Button
              onClick={() => {
                if (selectedCandidate && handleSaveFirstTimeName) {
                  handleSaveFirstTimeName(selectedCandidate);
                  setNameSelectOpen(false);
                }
              }}
              disabled={!selectedCandidate}
              className="w-full h-10 rounded-xl btn-primary-teal text-slate-950 font-black text-sm shadow-xs cursor-pointer"
            >
              <Check className="mr-1.5" size={16} strokeWidth={3} />
              {t.confirmThisName}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
